import * as z from 'zod/mini';
import { audioStates } from '../models/audio-state';
import { EpisodeCompoundId } from '../models/podcasts';
import { isEpisodeIdentifier } from '../utils/helpers';

//
// Schemas for native app state
//

// Some dates for timestamp sanity checks
const timestampMsRange = {
  min: new Date('Wed Jan 28 2026').getTime(),
  max: new Date('Wed Jan 28 2036').getTime(),
};
const timestampSchema = z
  .int()
  .check(z.gt(timestampMsRange.min), z.lt(timestampMsRange.max));

const episodeIdSchema = z.custom<EpisodeCompoundId>((val) => isEpisodeIdentifier(val));

const nativeStateEpisodeAdSchema = z.object({
  id: episodeIdSchema,
  ads: z.array(
    z
      .object({
        startTime: z.number(),
        endTime: z.number(),
        skipCostCredits: z.int().check(z.gte(0)),
        wasRecentlySkipped: z.boolean(),
      })
      .check(z.refine((ad) => ad.endTime > ad.startTime))
  ),
  position: z.number().check(z.gte(0)),
});

export const nativeAppStateV1Schema = z.object({
  // General state
  appVersion: z.string(),
  userId: z.string(),
  initializedAt: timestampSchema,
  updatedAt: timestampSchema,
  source: z.enum(['initial', 'during-playback', 'state-changed', 'activity-paused']),

  // Playback state
  state: z.enum(audioStates),
  episodes: z.array(nativeStateEpisodeAdSchema),
  sleepTimer: z._default(
    z.union([
      z.literal(''),
      z.literal('end-of-episode'),
      // This timestamp is stored as a string in Android b/c data class properties have a single type
      z.pipe(z.coerce.number(), timestampSchema),
    ]),
    ''
  ),

  // Tracking ad-skips and usage
  creditsLeft: z.int().check(z.gte(0)),
  untrackedAdSkips: z._default(
    z.array(
      z
        .object({
          episodeId: episodeIdSchema,
          startTime: z.number(),
          endTime: z.number(),
          skipAttemptedAt: timestampSchema,
          skipFailedReason: z.nullable(z.string()),
        })
        .check(z.refine((ad) => ad.endTime > ad.startTime))
    ),
    []
  ),
  untrackedSeconds: z._default(
    z.record(episodeIdSchema, z.number().check(z.gte(0))),
    {}
  ),
  untrackedCompletions: z._default(z.record(episodeIdSchema, timestampSchema), {}),
});

export type NativeState = z.infer<typeof nativeAppStateV1Schema>;
export type NativeStateType = 'current' | 'last-session';
